import { CountryViewModel, EmploymentViewModel } from '@fora-monorepo/employer-my-pages-common/types';
import { Assets, Box, Button, Container, Feedback, Flex, Headings, Line, Link, Tag } from '@foradevelopment/fora-ui';

import { AddressDetails } from './AddressDetails';
import { PersonDetails } from './PersonDetails';
import { MarkupAndParametersListOld } from './MarkupAndParametersListOld';
import { getCountryFromCode } from '../../common/utils';
import { useTypedTranslation } from '../../i18n/i18n';
import { useAppSelector } from '../../redux/hooks';

// TODO: REMOVE FILE MANAGE EMPLOYMENTS

export type EmploymentConfirmationDetailsSettingsOld = {
  /** The context of the employment confirmation details */
  confirmationType: 'add' | 'edit' | 'terminate';
  /** The new/updated/terminated employment */
  employment: EmploymentViewModel;
  /** Available country codes, needed for presenting country name */
  countries: CountryViewModel[];
  /** Whether to show the address in the confirmation */
  showAddress: boolean;
  /** Whether to show the information text that the data sent was of an existing employment */
  showExistingDuplicateInfo: boolean;
};

type EmploymentConfirmationDetailsProps = {
  allEmploymentsLink: string;
  addEmploymentLink?: string;
  goBackToEditPanel?: () => void;
  resetPanels?: () => void;
} & EmploymentConfirmationDetailsSettingsOld;

export const EmploymentConfirmationDetailsOld = ({
  allEmploymentsLink,
  addEmploymentLink = '',
  employment,
  countries,
  confirmationType,
  showAddress,
  showExistingDuplicateInfo,
  goBackToEditPanel,
  resetPanels,
}: EmploymentConfirmationDetailsProps) => {
  const { t } = useTypedTranslation();
  const ffManageEmployeeAndEmployment = useAppSelector(
    state => state.foraGlobal.settings.ffManageEmployeeAndEmployment
  );
  return (
    <Container maxWidth={578} width="100%">
      <Headings.H3 marginBottom={40}>
        {confirmationType === 'edit'
          ? t('panel.employeeAddedOrUpdated.confirmationEditText')
          : confirmationType === 'terminate'
            ? t('panel.terminateEmployment.confirmationTerminationText')
            : t('panel.employeeAddedOrUpdated.confirmationAddedText')}
      </Headings.H3>
      {confirmationType === 'edit' ? (
        <Flex justifyContent="space-between" width="100%" alignItems="flex-start" columnGap={20}>
          <PersonDetails {...employment.employee} headingSize="H4" />
          <Button styleAs="link" resetFlexProps onClick={goBackToEditPanel}>
            {t('common.edit')} <Assets.EditIcon marginLeft={5} />
          </Button>
        </Flex>
      ) : (
        <PersonDetails {...employment.employee} headingSize="H4" />
      )}
      {employment.employmentEndDate && (
        <Container marginTop={10}>
          <Tag
            text={t('section.employeeDetails.employmentEndsTag', { endDate: employment.employmentEndDate })}
            variant="informational"
          />
        </Container>
      )}
      {showAddress && (
        <Container marginTop={40}>
          <AddressDetails
            {...employment.employee.address}
            country={getCountryFromCode(countries, employment.employee.address.countryCode)}
          />
        </Container>
      )}
      {!ffManageEmployeeAndEmployment && (
        <Container marginTop={40}>
          <MarkupAndParametersListOld markups={employment.markups} activeMarkup={employment.activeMarkup} />
        </Container>
      )}

      {showExistingDuplicateInfo && (
        <Container marginTop={40}>
          <Feedback type="warning">{t('panel.employeeAddedOrUpdated.existingDuplicateConfirmationText')}</Feedback>
        </Container>
      )}
      <Container marginTop={60}>
        {confirmationType === 'add' ? (
          <Box>
            <Line marginBottom={60} color="black10" />
            <Flex rowGap={20} columnGap={20} flexWrap="wrap">
              <Link styleAs="button" onClick={resetPanels} to={addEmploymentLink} hasIcon={false}>
                {t('panel.employeeAddedOrUpdated.buttonTextAddMoreEmployments')}
              </Link>
              <Link variant="outline" styleAs="button" to={allEmploymentsLink} hasIcon={false}>
                {t('panel.employeeAddedOrUpdated.buttonTextShowAllEmployments')}
              </Link>
            </Flex>
          </Box>
        ) : (
          <Link variant="outline" styleAs="button" to={allEmploymentsLink} hasIcon={false}>
            {t('panel.employeeAddedOrUpdated.buttonTextShowAllEmployments')}
          </Link>
        )}
      </Container>
    </Container>
  );
};
